import { createSlice, createAction, createAsyncThunk, createListenerMiddleware, TypedStartListening } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { generateInitialState } from "utils/common";
import * as api from "api/api";
import { ApplicationState, AppDispatch, resetApp } from "./store";
import { authoriseSuccess } from "./authorisation";

const sliceName = "company";

// Middlewares
export const companyListener = createListenerMiddleware();
export const startCompanyListener = companyListener.startListening as TypedStartListening<ApplicationState, AppDispatch>;

// Thunks
export const getCompany = createAsyncThunk<BokaMera.Company, string, { state: ApplicationState }>(
    `${sliceName}/GET_COMPANY`,
    async (companyId, thunkApi) => {
        try {
            const response = await api.getCompanies({
                params: {
                    Id: companyId,
                    IncludeSystemSettings: true,
                    IncludeBookingAgreements: true,
                },
            })
                ;
            return thunkApi.fulfillWithValue(response.Results[0])
        } catch (error) {
            return thunkApi.rejectWithValue((error as AxiosError)?.response?.statusText);
        }
    }
);

// Actions
export const setCompany = createAction(`${sliceName}/SET_COMPANY`, (company: BokaMera.Company) => {
    return {
        payload: company
    }
});

// Slice
const initialState = generateInitialState<BokaMera.Company>();

const companySlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addMatcher(setCompany.match, (state, { payload }) => {
            state.entity = payload;
        });

        builder.addMatcher(getCompany.pending.match, (state) => {
            state.isInitialized = true;
            state.isLoading = true;
        });

        builder.addMatcher(getCompany.fulfilled.match, (state, { payload }) => {
            state.isSuccess = true;
            state.isInitialized = true;
            state.isLoading = false;
            state.isError = false;

            state.entity = payload;

        });

        builder.addMatcher(getCompany.rejected.match, (state, { payload }) => {
            state.isLoading = false;
            state.isError = true;
            state.error = payload;
        });

        builder.addMatcher(resetApp.match, () => {
            return initialState;
        });
    },
});

// Listeners
startCompanyListener({
    matcher: authoriseSuccess.match,
    effect: async ({ payload }, api) => {
        const companyState = api.getState().company;

        if (!companyState.isLoading && !companyState.entity) {
            const companyId = payload.AdminProfile.CompanyId;
            api.dispatch(
                getCompany(companyId)
            )
        }
    }
})

//
// Selectors
export const isCompanyLoading = (s: ApplicationState) => Boolean(s.company.isLoading);

export default companySlice;
